<template>
    <q-card class="card no-shadow border-grey">
        <!-- Header -->
        <q-card-section
            class="text-white card__header"
            v-bind:style="{ background: widgetColor }"
        >
            <q-badge
                v-if="badgeType"
                class="widget-badge"
                rounded
                :color="badgeType === 'new' ? 'light-green' : 'dark-yellow'"
                :label="getBadgeContent"
            />
            <div class="row">
                <q-btn
                    v-if="isEdit"
                    @click="$emit('edit')"
                    class="col-1"
                    icon="fas fa-edit"
                    flat
                >
                    <q-tooltip>{{ $t('Edit widget') }}</q-tooltip>
                </q-btn>
                <div class="col text-center card__header-text ellipsis">
                    <div>{{ getWidgetTitle }}</div>
                    <div>{{ getWidgetSubtitle }}</div>
                </div>
                <q-btn
                    v-if="isEdit"
                    @click="$emit('remove')"
                    class="col-1"
                    icon="fas fa-times"
                    color="red"
                    dense
                >
                    <q-tooltip>{{ $t('Remove widget') }}</q-tooltip>
                </q-btn>
            </div>
        </q-card-section>

        <!-- Body -->
        <q-card-section class="q-mt-lg q-mb-lg text-center card__body">
            <div class="card__body--counter">
                <div class="card__body-digits">
                    {{ displayedValue }}
                </div>
                <br />
                <div class="card__body-units">
                    {{ displayedUnit }}
                </div>
            </div>
        </q-card-section>
    </q-card>
</template>

<script>
import { convertToUnitSystem } from '@models/products.js';
import { CALCULATED_WIDGETS, WIDGET_TYPE_TO_PROPS } from '@models/widgets.js';
import { getCssVar } from 'quasar';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'AssetWidget',
    emits: ['remove', 'edit'],
    props: {
        /**
         * Live stream asset data.
         */
        data: {
            type: Object,
            required: false,
            default: null
        },
        /**
         * Widget type.
         */
        type: {
            type: String,
            required: true
        },
        /**
         * Whether to show the content of the widget.
         */
        showValue: {
            type: Boolean,
            required: false,
            default: true
        },
        /**
         * If this widget is on edit mode.
         */
        isEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        speedWidgetColorLevels: {
            type: Array,
            required: false,
            default: null
        },
        expectedRunRate: {
            type: Number,
            required: false,
            default: null
        },
        sensorConfiguration: {
            type: String,
            required: true
        },
        packagingFactors: {
            type: Object,
            required: true
        },
        productUnitPackaging: {
            type: Number,
            required: false,
            default: 1
        },
        showBadge: {
            type: Boolean,
            required: false,
            default: false
        },
        isDuplicate: {
            type: Boolean,
            required: false,
            default: false
        },
        badgeType: {
            type: String,
            required: false,
            default: ''
        }
    },
    data() {
        return {
            // get card values based on widget type
            WIDGET_TYPE_TO_PROPS: WIDGET_TYPE_TO_PROPS,
            CALCULATED_WIDGETS: CALCULATED_WIDGETS,
            // oee widgets type
            oeeWidgets: [
                'quality_current_day',
                'quality_yesterday',
                'quality_current_shift',
                'quality_current_product',
                'quality_1min',
                'quality_5min',
                'quality_60min',
                'performance_current_day',
                'performance_yesterday',
                'performance_current_product',
                'performance_current_shift',
                'performance_1min',
                'performance_5min',
                'performance_60min',
                'uptime_current_day',
                'uptime_yesterday',
                'uptime_current_product',
                'uptime_current_shift',
                'uptime_1min',
                'uptime_5min',
                'uptime_60min',
                'oee_current_day',
                'oee_yesterday',
                'oee_current_shift',
                'oee_current_product',
                'oee_1min',
                'oee_5min',
                'oee_60min'
            ],
            // oee default text color
            oeeDefaultColorLevel: [
                {
                    lower: -Number.MAX_VALUE,
                    upper: 50,
                    color: getCssVar('negative')
                }, // negative
                {
                    lower: 50,
                    upper: 75,
                    color: getCssVar('warning')
                }, // warning
                {
                    lower: 75,
                    upper: Number.MAX_VALUE,
                    color: getCssVar('positive')
                } // positive
            ],
            // default widget text color
            defaultWidgetColor: getCssVar('accent'),
            // speed widgets type
            speedWidgets: [
                'speed_count_1min',
                'speed_count_5min',
                'speed_count_60min',
                'speed_count_1min_per_h',
                'speed_count_5min_per_h',
                'speed_count_60min_per_h',
                'speed_quantity_1min',
                'speed_quantity_5min',
                'speed_quantity_60min',
                'speed_quantity_1min_per_h',
                'speed_quantity_5min_per_h',
                'speed_quantity_60min_per_h'
            ],
            // Hourly speed widgets type
            speedHourlyWidgets: [
                'speed_count_1min_per_h',
                'speed_count_5min_per_h',
                'speed_count_60min_per_h',
                'speed_quantity_1min_per_h',
                'speed_quantity_5min_per_h',
                'speed_quantity_60min_per_h'
            ],
            // package speed widgets
            packageSpeedWidgets: [
                'package_speed_1min_per_min',
                'package_speed_5min_per_min',
                'package_speed_60min_per_min',
                'package_speed_1min_per_h',
                'package_speed_5min_per_h',
                'package_speed_60min_per_h'
            ],
            packageSpeedHourlyWidgets: [
                'package_speed_1min_per_h',
                'package_speed_5min_per_h',
                'package_speed_60min_per_h'
            ],
            /**
             * Average quantity widgets type
             * The calculations for the average quantity widgets are
             * based on the product unit packaging.
             */
            averageQuantityWidgets: [
                'speed_quantity_1min',
                'speed_quantity_5min',
                'speed_quantity_60min',
                'speed_quantity_1min_per_h',
                'speed_quantity_5min_per_h',
                'speed_quantity_60min_per_h'
            ]
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, {
            getSelectedFacility: 'getSelectedFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get current facility
         * @return {Object} facility object
         */
        facility() {
            return this.getSelectedFacility;
        },
        /**
         * Get the widget color
         * @return {String}
         */
        widgetColor() {
            if (this.speedWidgets.includes(this.type)) {
                // speed widgets
                return this.speedWidgetColor();
            } else if (this.packageSpeedWidgets.includes(this.type)) {
                // package speed widgets
                return this.packageSpeedWidgetColor();
            } else if (this.oeeWidgets.includes(this.type)) {
                // oee widgets
                return this.oeeWidgetColor();
            } else {
                // return default color if data is not streaming or it is not speed or oee widget
                return this.defaultWidgetColor;
            }
        },
        /**
         * Get the expected package run rate
         * using the sensor configuration and packaging factors
         * @return {Number} Expected package run rate
         */
        expectedPackageRunRate() {
            if (
                this.sensorConfiguration === 'gross-reject' ||
                this.sensorConfiguration === 'gross-net'
            ) {
                // gross-reject or gross-net configuration
                return (
                    this.expectedRunRate / this.packagingFactors['production']
                );
            } else {
                // net-reject configuration
                return (
                    this.expectedRunRate /
                    ((this.packagingFactors['net'] +
                        this.packagingFactors['reject']) /
                        2)
                );
            }
        },
        /**
         * Event property to use for this card.
         * @return {String}
         */
        property() {
            return WIDGET_TYPE_TO_PROPS[this.type]['property'];
        },
        /**
         * Get the data type to use for this card value.
         * @return {String}
         */
        dataType() {
            return WIDGET_TYPE_TO_PROPS[this.type]['type'];
        },
        /**
         * Get widget value.
         * @return {Number} Widget value
         */
        widgetValue() {
            if (this.data === null) {
                return null;
            }
            // check if data has property
            if (
                !(this.property in this.data) &&
                !(this.property in CALCULATED_WIDGETS)
            ) {
                return null;
            }

            // if data has property, check if value is given
            const value = this.data[this.property]['value'];
            if (!isNaN(value) && value !== null) {
                return value;
            }

            return null;
        },
        /**
         * Value to display on widget.
         * @return {String, Number} Empty value placeholder or formatted number.
         */
        displayedValue() {
            if (this.showValue) {
                // Format widget value if not null or negative
                if (this.widgetValue !== null && this.widgetValue >= 0) {
                    return this.formatNumber(this.widgetValue);
                }
            }
            return '-';
        },
        /**
         * Original unit of the widget. (e.g. kg or litre for quantities) before conversion.
         * @return {String} - Unit of the widget.
         */
        widgetUnit() {
            if (this.data !== null) {
                if (this.property in this.data) {
                    return this.data[this.property]['unit'];
                }
            } else {
                // check if in edit mode
                if (this.isEdit) {
                    // use default unit label
                    return WIDGET_TYPE_TO_PROPS[this.type]['unitLabel'];
                }
            }
            return '';
        },
        /**
         * Unit to display on the widget. Converted to the facility unit system.
         * @return {String} - Unit to display
         */
        displayedUnit() {
            if (this.widgetUnit) {
                // replace the original unit with the converted unit
                return this.widgetUnit.replace(
                    this.productUnit,
                    this.convertedUnits?.unit
                );
            }

            return '';
        },
        /**
         * Returns true if the widget should use the alternative titles.
         * @return {Boolean}
         */
        useAltTitles() {
            // if this is a "package" count widget
            if (
                [
                    'quantity_package_count',
                    'quantity_net_package_count',
                    'quantity_reject_package_count',
                    'package_speed_1min_per_min',
                    'package_speed_5min_per_min',
                    'package_speed_60min_per_min',
                    'package_speed_1min_per_h',
                    'package_speed_5min_per_h',
                    'package_speed_60min_per_h'
                ].includes(this.type)
            ) {
                // if using packaging factors, check for alt title
                if (
                    // using packaging factors if at least one of the packaging factors is not 1
                    this.packagingFactors['production'] !== 1.0 ||
                    this.packagingFactors['net'] !== 1.0 ||
                    this.packagingFactors['reject'] !== 1.0
                ) {
                    // the product of the packaging factors is a number smaller than or equal to 1,
                    // so we are counting product units (not packages)
                    if (
                        this.packagingFactors['production'] *
                            this.packagingFactors['net'] *
                            this.packagingFactors['reject'] <=
                        1.0
                    ) {
                        return true;
                    }
                }
            }

            // all other cases, use default title
            return false;
        },
        /**
         * Convert value and unit to the facility unit system
         * @return {Object} - Converted value and unit. { value, unit }
         */
        convertedUnits() {
            const { value, unit = '' } = convertToUnitSystem(
                this.widgetValue,
                this.productUnit,
                this.facility?.unit_system
            );
            return { value, unit };
        },
        /**
         * Get the product unit used in the widget, if any.
         * For some widgets, the unit provided is in a form of a rate (e.g. string 'kg/min').
         * This function returns the pure unit (e.g. kg, litre) of the product, which is needed
         * for conversion to the facility unit system.
         * @return {String} - Product unit as included in the widget unit (kg / litre)
         */
        productUnit() {
            const product_unit = this.widgetUnit.match(/kg|litre/gi);
            return product_unit ? product_unit[0] : '';
        },
        /**
         * Get the title of the widget
         * @return {String}
         */
        getWidgetTitle() {
            const title = this.useAltTitles ? 'altTitle1' : 'title1';
            return this.$t(WIDGET_TYPE_TO_PROPS[this.type][title]);
        },
        /**
         * Get the subtitle of the widget
         * @return {String}
         */
        getWidgetSubtitle() {
            const title = this.useAltTitles ? 'altTitle2' : 'title2';
            return this.$t(WIDGET_TYPE_TO_PROPS[this.type][title]);
        },
        /**
         * Get the badge content
         * @return {String}
         */
        getBadgeContent() {
            if (this.badgeType === 'duplicate') {
                return this.$t('Duplicate');
            }
            return this.$t('New');
        }
    },
    methods: {
        /**
         * Formats number based on data type defined in widget model.
         * @param {Number} number
         * @return {String} Formatted number based on data type in widget model.
         */
        formatNumber(number) {
            // if the widget type contains quantity,
            // first convert the value to the unit system of the facility
            if (this.type.includes('quantity')) {
                number = this.convertedUnits?.value;
            }

            switch (this.dataType) {
                case 'integer':
                    return number.toLocaleString(this.userLocale);

                case 'float':
                    return number.toLocaleString(this.userLocale, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1
                    });

                case 'time':
                    // convert number of seconds into format 'hh:mm:ss'
                    return this.toDHMSformat(number);

                default:
                    return number;
            }
        },
        /**
         * Format number of seconds into d h m s format (e.g. '2d 17h 32m 16s')
         * @param {Number} seconds - Total seconds to allocate to time units
         * @return {String} Time elapsed in format d h m s
         */
        toDHMSformat(n) {
            // display a value if no time elapsed
            if (n === 0) {
                return '0s';
            }

            // allocate seconds
            const days = Math.floor(n / (24 * 3600));
            n = n % (24 * 3600);

            const hours = Math.floor(n / 3600);
            n = n % 3600;

            const minutes = Math.floor(n / 60);
            n = n % 60;

            const seconds = n;

            // build string
            let output = '';
            if (days) {
                output += `${days}d `;
            }
            if (hours) {
                output += `${hours}h `;
            }
            if (minutes) {
                output += `${minutes}m `;
            }
            if (seconds) {
                output += `${seconds}s`;
            }

            return output;
        },
        /**
         * Calculate percentage between actual run rate and expected run rate
         * @param {Number} runningRate - Actual run rate
         * @param {Number} expectedRunRate - Expected run rate
         * @return {Number} percentage
         */
        calculatePercentage(runningRate, expectedRunRate) {
            if (expectedRunRate === 0 || runningRate === 0) {
                return 0;
            }

            if (this.averageQuantityWidgets.includes(this.type)) {
                expectedRunRate = expectedRunRate * this.productUnitPackaging;
            }

            const percentage = (runningRate / expectedRunRate) * 100;
            return Math.round(percentage * 100) / 100;
        },
        /**
         * Calculate hour percentage between actual run rate and expected run rate
         * @param {Number} runningRate - Actual run rate
         * @param {Number} expectedRunRate - Expected run rate
         * @return {Number} percentage
         */
        calculateHourlyPercentage(runningRate, expectedRunRate) {
            if (expectedRunRate === 0 || runningRate === 0) {
                return 0;
            }

            if (this.averageQuantityWidgets.includes(this.type)) {
                expectedRunRate = expectedRunRate * this.productUnitPackaging;
            }

            const expectedRunRatePerHour = expectedRunRate * 60;
            const percentage = (runningRate / expectedRunRatePerHour) * 100;
            return Math.round(percentage * 100) / 100;
        },
        /**
         * Calculate color based on level
         * @param {Number} runningPercentage
         * @return {String} level color
         */
        calculateColor(runningPercentage) {
            // If speed is higher than the last upper value,
            // return the color of the highest range
            const lastLevel =
                this.speedWidgetColorLevels[
                    this.speedWidgetColorLevels.length - 1
                ];
            if (runningPercentage >= lastLevel.upper) {
                return lastLevel.color;
            }

            // get the color based on level percentage
            for (let i = 0; i < this.speedWidgetColorLevels.length; i++) {
                const level = this.speedWidgetColorLevels[i];
                if (
                    runningPercentage >= level.lower &&
                    runningPercentage < level.upper
                ) {
                    return level.color;
                }
            }
            return this.defaultWidgetColor;
        },
        /**
         * Get the speed widget color
         * @return {String}
         */
        speedWidgetColor() {
            const widgetValue = parseFloat(this.widgetValue);

            if (isNaN(widgetValue)) {
                return this.defaultWidgetColor;
            }

            const isHourlyWidget = this.speedHourlyWidgets.includes(this.type);
            const percentage = isHourlyWidget
                ? this.calculateHourlyPercentage(
                      widgetValue,
                      this.expectedRunRate
                  )
                : this.calculatePercentage(widgetValue, this.expectedRunRate);

            return this.calculateColor(percentage);
        },
        /**
         * Get the speed widget color
         * @return {String}
         */
        packageSpeedWidgetColor() {
            const widgetValue = parseFloat(this.widgetValue);

            if (isNaN(widgetValue)) {
                return this.defaultWidgetColor;
            }

            const isHourlyWidget = this.packageSpeedHourlyWidgets.includes(
                this.type
            );

            const percentage = isHourlyWidget
                ? this.calculateHourlyPercentage(
                      widgetValue,
                      this.expectedPackageRunRate
                  )
                : this.calculatePercentage(
                      widgetValue,
                      this.expectedPackageRunRate
                  );
            return this.calculateColor(percentage);
        },
        /**
         * Get the oee widget color
         * @return {String}
         */
        oeeWidgetColor() {
            if (!isNaN(this.widgetValue)) {
                for (const level of this.oeeDefaultColorLevel) {
                    const lower = level['lower'];
                    const upper = level['upper'];
                    if (
                        this.widgetValue >= lower &&
                        this.widgetValue <= upper
                    ) {
                        return level['color'];
                    }
                }
            }
            return getCssVar('primary');
        }
    }
};
</script>

<style lang="scss" scoped>
.card__header {
    padding: 4px;
}

.card__header-text {
    font-size: 18px;
    font-weight: 500;
}

.card__body {
    height: 150px;
    padding: 0px;
    margin: 0px;
}

.card__body-digits {
    font-size: 35px;
    font-weight: 900;
}

.card__body-units {
    font-size: 25px;
    font-weight: 500;
}

.card__body--counter {
    // center item
    position: relative;
    top: 50%;
    transform: translateY(-50%);
}

.widget-badge {
    position: absolute;
    z-index: 10;
    top: -15px;
    right: 50%;
    padding: 6px 10px;
    transform: translateX(50%);
}
</style>
